import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Switch,
  Modal,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, fontSize, ThemeColors, radius } from '../theme';
import { profilePalette } from '../theme/profileSection';
import ProfileSection from '../components/ProfileSection';
import SectionActionRow from '../components/SectionActionRow';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import { useTranslation } from '../i18n/I18nContext';
import { parseDecimalInput } from '../i18n/decimalInput';
import { api, ContactPriceKind, ProfileAccount, ProfileEmployer } from '../services/api';
import { formatMoney, formatPerMinute } from '../data/currencies';
import Avatar from '../components/Avatar';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import VerifiedBadge from '../components/VerifiedBadge';
import { buildProfileIdentityItems } from '../utils/profileIdentity';
import {
  loadContactTranslationPreference,
  saveContactTranslationPreference,
} from '../services/contactPreferences';

type User = {
  /** 9.26 / D-641 — resmî hesap (mavi işaret). */
  is_official?: boolean;
  id: number;
  name: string;
  surname: string | null;
  username: string | null;
  photo_url: string | null;
  university: string | null;
  profession: string | null;
  price_message: number;
  price_voice: number;
  price_call: number;
  price_call_scheduled?: number;
  price_time: number;
  currency: string;
  bio: string | null;
  employer: ProfileEmployer | null;
  university_account: ProfileAccount | null;
};

type Props = {
  userId: number;
  myId: number;
  token: string;
  onBack: () => void;
  onBlocked: () => void;
  onMessage: (name: string, photo: string | null) => void;
  onPlanCall: (name: string, photo: string | null) => void;
  onProposeMeetup: (name: string, photo: string | null) => void;
  onOpenProfile: (userId: number) => void;
};

type PreferenceFeedback = {
  state: 'saving' | 'saved' | 'error';
  retryValue: boolean | null;
} | null;

// D-302 — kişiye özel fiyatın mesaj dışındaki türleri. Sıra ekranda göründüğü
// sıradır: önce asenkron (ses kaydı), sonra eşzamanlı (anlık/planlı arama), en
// sonda yüz yüze (buluşma) — taahhüt düzeyi arttıkça aşağı.
//
// Etiketler `profile.*` altındaki MEVCUT anahtarlardır, yeni değil: kullanıcı
// kendi genel fiyatlarını zaten bu adlarla görüyor. Aynı şey için ikinci bir
// sözcük seti üretmek, "Şimdi ara" ile "Anlık arama"yı iki ayrı ürün gibi
// gösterirdi.
const CONTACT_PRICE_KINDS: { key: ContactPriceKind; labelKey: string }[] = [
  { key: 'price_voice', labelKey: 'profile.priceVoice' },
  { key: 'price_call', labelKey: 'profile.priceCall' },
  { key: 'price_call_scheduled', labelKey: 'profile.priceScheduledCall' },
  { key: 'price_time', labelKey: 'profile.priceTime' },
];

export default function UserProfileScreen({ userId, myId, token, onBack, onBlocked, onMessage, onPlanCall, onProposeMeetup, onOpenProfile }: Props) {
  const insets = useSafeAreaInsets();
  const { t, lang } = useTranslation();
  const colors = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  // 9.4.7.5 kişi istisnası (D-227): null = genel politika, true/false = istisna.
  const [callAllowed, setCallAllowed] = useState<boolean | null>(null);
  const [callAllowedSaving, setCallAllowedSaving] = useState(false);
  const [messageAllowed, setMessageAllowed] = useState<boolean | null>(null);
  const [messageAllowedSaving, setMessageAllowedSaving] = useState(false);
  const [contactPreferencesState, setContactPreferencesState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [contactPreferencesFor, setContactPreferencesFor] = useState('');
  const [callAllowedFeedback, setCallAllowedFeedback] = useState<PreferenceFeedback>(null);
  const [messageAllowedFeedback, setMessageAllowedFeedback] = useState<PreferenceFeedback>(null);
  const contactPreferencesRequestRef = useRef(0);
  const contactContextKey = `${token}:${userId}`;
  const contactContextRef = useRef(contactContextKey);
  contactContextRef.current = contactContextKey;
  const [translationSaving, setTranslationSaving] = useState(false);
  // D-305 — engel durumu sunucudan okunur, yerel bir bayrak değildir: kullanıcı
  // engelleyip çıksa ve geri gelse buton yine "Engeli kaldır" demelidir.
  const [blocked, setBlocked] = useState(false);
  const [blockSaving, setBlockSaving] = useState(false);
  const [customPrice, setCustomPrice] = useState<number | null>(null);
  // D-302 — mesaj dışındaki dört türün kişiye özel fiyatı. null = özel fiyat
  // yok, satıcının genel fiyatı geçerli.
  const [customExtras, setCustomExtras] =
    useState<Partial<Record<ContactPriceKind, number | null>>>({});
  const [extraInputs, setExtraInputs] = useState<Record<string, string>>({});
  const [contactPriceLoading, setContactPriceLoading] = useState(true);
  const [contactPriceFailed, setContactPriceFailed] = useState(false);
  const [priceModal, setPriceModal] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [priceSaving, setPriceSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    api.getUser(userId, lang, token)
      .then(setUser)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [userId, lang]);

  useEffect(() => {
    load();
  }, [load]);

  const loadContactPrice = useCallback(() => {
    let active = true;
    setContactPriceLoading(true);
    setContactPriceFailed(false);
    api.getContactPrice(token, userId)
      .then((result) => {
        if (!active) return;
        setCustomPrice(result.price === null ? null : Number(result.price));
        setCustomExtras({
          price_voice: result.price_voice ?? null,
          price_call: result.price_call ?? null,
          price_call_scheduled: result.price_call_scheduled ?? null,
          price_time: result.price_time ?? null,
        });
      })
      .catch(() => {
        if (active) setContactPriceFailed(true);
      })
      .finally(() => {
        if (active) setContactPriceLoading(false);
      });
    return () => { active = false; };
  }, [token, userId]);

  useEffect(() => loadContactPrice(), [loadContactPrice]);

  useEffect(() => {
    let active = true;
    api.getBlockStatus(token, userId)
      .then((result) => { if (active) setBlocked(result.blocked); })
      // Okunamazsa engelli SAYMA: yanlış "engeli kaldır" göstermek, kullanıcıya
      // var olmayan bir engeli kaldırttırır ve gerçek durumu gizler.
      .catch(() => { if (active) setBlocked(false); });
    return () => { active = false; };
  }, [token, userId]);

  // 9.4.7.5 kişi istisnası (D-227). Çeviriden ayrı okunuyor: çeviri tercihi
  // çevrimdışı aynası olan kendi servisinden geliyor, istisna ise yalnız sunucuda
  // yaşıyor (kapıyı sunucu uyguluyor, yerel kopya yanıltıcı olurdu).
  const loadContactPreferences = useCallback(async () => {
    const requestId = ++contactPreferencesRequestRef.current;
    const contextKey = `${token}:${userId}`;
    setContactPreferencesState('loading');
    setContactPreferencesFor('');
    setCallAllowedSaving(false);
    setMessageAllowedSaving(false);
    setTranslationSaving(false);
    setCallAllowedFeedback(null);
    setMessageAllowedFeedback(null);
    try {
      const preferences = await api.getContactPreferences(token, userId);
      if (requestId !== contactPreferencesRequestRef.current
        || contactContextRef.current !== contextKey) return;
      const translation = await loadContactTranslationPreference(
        token,
        myId,
        userId,
        preferences,
      );
      if (requestId !== contactPreferencesRequestRef.current
        || contactContextRef.current !== contextKey) return;
      setTranslationEnabled(translation);
      setCallAllowed(preferences.call_allowed ?? null);
      setMessageAllowed(preferences.message_allowed ?? null);
      setContactPreferencesFor(contextKey);
      setContactPreferencesState('ready');
    } catch {
      if (requestId === contactPreferencesRequestRef.current
        && contactContextRef.current === contextKey) {
        loadContactTranslationPreference(token, myId, userId, null)
          .then((cached) => {
            if (requestId === contactPreferencesRequestRef.current
              && contactContextRef.current === contextKey) setTranslationEnabled(cached);
          })
          .catch(() => {});
        setContactPreferencesState('error');
      }
    }
  }, [token, myId, userId]);

  useEffect(() => {
    loadContactPreferences();
    return () => { contactPreferencesRequestRef.current += 1; };
  }, [loadContactPreferences]);

  const contactPreferencesReady = contactPreferencesState === 'ready'
    && contactPreferencesFor === contactContextKey;

  async function changeCallAllowed(next: boolean | null) {
    if (!contactPreferencesReady || callAllowedSaving) return;
    const previous = callAllowed;
    const contextKey = contactContextKey;
    setCallAllowed(next);
    setCallAllowedSaving(true);
    setCallAllowedFeedback({ state: 'saving', retryValue: next });
    try {
      const saved = await api.updateContactPreferences(token, userId, { call_allowed: next });
      if (contactContextRef.current !== contextKey) return;
      setCallAllowed(saved.call_allowed ?? null);
      setCallAllowedFeedback({ state: 'saved', retryValue: next });
    } catch {
      if (contactContextRef.current !== contextKey) return;
      setCallAllowed(previous);
      setCallAllowedFeedback({ state: 'error', retryValue: next });
    } finally {
      if (contactContextRef.current === contextKey) setCallAllowedSaving(false);
    }
  }

  async function changeMessageAllowed(next: boolean | null) {
    if (!contactPreferencesReady || messageAllowedSaving) return;
    const previous = messageAllowed;
    const contextKey = contactContextKey;
    setMessageAllowed(next);
    setMessageAllowedSaving(true);
    setMessageAllowedFeedback({ state: 'saving', retryValue: next });
    try {
      const saved = await api.updateContactPreferences(token, userId, { message_allowed: next });
      if (contactContextRef.current !== contextKey) return;
      setMessageAllowed(saved.message_allowed ?? null);
      setMessageAllowedFeedback({ state: 'saved', retryValue: next });
    } catch {
      if (contactContextRef.current !== contextKey) return;
      setMessageAllowed(previous);
      setMessageAllowedFeedback({ state: 'error', retryValue: next });
    } finally {
      if (contactContextRef.current === contextKey) setMessageAllowedSaving(false);
    }
  }

  async function changeTranslation(enabled: boolean) {
    const previous = translationEnabled;
    const contextKey = contactContextKey;
    setTranslationEnabled(enabled);
    setTranslationSaving(true);
    try {
      const saved = await saveContactTranslationPreference(token, myId, userId, enabled);
      if (contactContextRef.current !== contextKey) return;
      setTranslationEnabled(saved);
    } catch {
      if (contactContextRef.current !== contextKey) return;
      setTranslationEnabled(previous);
      Alert.alert(t('common.error'), t('message.saveError'));
    } finally {
      if (contactContextRef.current === contextKey) setTranslationSaving(false);
    }
  }

  function openPriceModal() {
    setPriceInput(customPrice === null ? '' : String(customPrice));
    // Boş girdi "bu türde özel fiyatım yok" demektir; sunucudaki null da öyle.
    setExtraInputs(Object.fromEntries(
      CONTACT_PRICE_KINDS.map(({ key }) => {
        const value = customExtras[key];
        return [key, value === null || value === undefined ? '' : String(value)];
      })
    ));
    setPriceModal(true);
  }

  async function saveContactPrice() {
    // Preserve the primary field's existing blank = free behaviour.
    const price = priceInput.trim() === '' ? 0 : parseDecimalInput(priceInput, lang);
    if (price === null) return;

    // Boş alan "bu türde özel fiyatım yok" demektir ve null olarak gider:
    // sunucu o türü kaldırır, kişi satıcının genel fiyatına döner. 0 ise
    // geçerli bir fiyattır ("bu kişiye ücretsiz") ve boşla karıştırılmaz.
    const extras: Partial<Record<ContactPriceKind, number | null>> = {};
    for (const { key } of CONTACT_PRICE_KINDS) {
      const raw = (extraInputs[key] ?? '').trim();
      if (raw === '') {
        extras[key] = null;
        continue;
      }
      const value = parseDecimalInput(raw, lang);
      if (value === null) return;
      extras[key] = value;
    }

    setPriceSaving(true);
    try {
      const saved = await api.setContactPrice(token, userId, price, extras);
      setCustomPrice(Number(saved.price));
      setCustomExtras({
        price_voice: saved.price_voice ?? null,
        price_call: saved.price_call ?? null,
        price_call_scheduled: saved.price_call_scheduled ?? null,
        price_time: saved.price_time ?? null,
      });
      setContactPriceFailed(false);
      setPriceModal(false);
      Alert.alert(
        t('message.saved'),
        price === 0
          ? t('message.priceFreeSet', { name: user?.name ?? '' })
          // D-416 — kişiye özel fiyat SATICININ para biriminde saklanır; sabit
          // `$` başka para birimindeki bir tutarı dolar gibi gösteriyordu.
          : t('message.priceSet', { name: user?.name ?? '', price: formatMoney(price, user?.currency) })
      );
    } catch {
      Alert.alert(t('common.error'), t('message.saveError'));
    } finally {
      setPriceSaving(false);
    }
  }

  // 3.1.6.3 — kişiyi listeden çıkar. Engelleme değil: kişi genel fiyata döner ve
  // genel politikaya tabi olur. Sonucu peşinen söylüyoruz, çünkü "yalnız kişiler"
  // ayarındaki bir kullanıcı için bu aynı zamanda erişimi kapatmak demek.
  function removeContactPrice() {
    if (!user) return;
    const { name } = user;
    Alert.alert(
      t('message.priceRemoveTitle', { name }),
      t('message.priceRemoveBody', { name }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('message.priceRemoveConfirm'),
          style: 'destructive',
          onPress: async () => {
            setPriceSaving(true);
            try {
              await api.removeContactPrice(token, userId);
              setCustomPrice(null);
              setCustomExtras({});
              setContactPriceFailed(false);
              setPriceModal(false);
              Alert.alert(t('message.saved'), t('message.priceRemoved', { name }));
            } catch {
              Alert.alert(t('common.error'), t('message.saveError'));
            } finally {
              setPriceSaving(false);
            }
          },
        },
      ]
    );
  }

  function doBlock() {
    if (!user) return;
    const { id, name } = user;
    Alert.alert(
      t('userProfile.blockTitle', { name }),
      t('userProfile.blockMessage'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('userProfile.blockConfirm'),
          style: 'destructive',
          onPress: async () => {
            try {
              await api.blockUser(token, id);
              // D-305 — sayfadan atmıyoruz. Eylemin sonucu, eylemin yapıldığı
              // yerde görünmelidir; buton "Engeli kaldır"a döner ve yanlışlıkla
              // basan tek dokunuşla geri alabilir. `onBlocked` üst ekrana haber
              // vermeyi sürdürür (listeler tazelensin diye).
              setBlocked(true);
              onBlocked();
            } catch {
              Alert.alert(t('common.error'), t('userProfile.failed'));
            }
          },
        },
      ]
    );
  }

  // Engeli kaldırmak onay istemez. Engelleme yıkıcı olduğu için onaylanır;
  // kaldırmak onun GERİ ALINMASIDIR ve tek dokunuşla yeniden engellenebilir.
  // Yanlışlıkla engelleyen birine, düzeltmek için ikinci bir kapı koymak
  // hatayı pahalılaştırır.
  async function doUnblock() {
    if (!user || blockSaving) return;
    setBlockSaving(true);
    try {
      await api.unblockUser(token, user.id);
      setBlocked(false);
    } catch {
      Alert.alert(t('common.error'), t('userProfile.failed'));
    } finally {
      setBlockSaving(false);
    }
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.black} />
      </View>
    );
  }

  if (failed || !user) {
    return (
      <View style={styles.container}>
        <PageHeader title="" onBack={onBack} backTestID="user-profile-back" />
        <View style={styles.center}>
          <Text style={styles.errorText}>{t('userProfile.failed')}</Text>
          <CompactActionButton
              variant="primary"
              label={t('common.retry')}
              onPress={load}
              style={styles.retryBtn}
              testID="user-profile-retry"
            />
        </View>
      </View>
    );
  }

  const identityItems = buildProfileIdentityItems({
    profession: user.profession,
    employer: user.employer,
    university: user.university,
    universityAccount: user.university_account,
  });

  return (
    <View style={styles.container}>
      {/* 7.6 / D-662 — ortak başlık (D-648): geri oku ve ad solda, sayfa kayarken
          yerinde durur. Önceden cam bir düğme ve büyük kalın bir ad vardı ve
          ikisi de içerikle birlikte kayıp gidiyordu. */}
      <PageHeader
        title={user.username ?? [user.name, user.surname].filter(Boolean).join(' ')}
        onBack={onBack}
        floating
        backTestID="user-profile-back"
      />
      <ScrollView
        contentContainerStyle={{
          paddingTop: screenHeaderContentInset(insets.top),
          paddingBottom: insets.bottom + spacing.xl,
        }}>

      <View style={styles.profileSummary}>
        <Avatar photoUrl={user.photo_url} name={user.name} size={104} style={styles.photo} />
        <View style={styles.summaryText}>
          <View style={styles.nameWithBadge}>
            <Text style={styles.name}>{[user.name, user.surname].filter(Boolean).join(' ')}</Text>
            {user.is_official && <VerifiedBadge size={20} />}
          </View>
        </View>
      </View>

      <View style={styles.info}>
        {identityItems.length > 0 && (
          <Text testID="profile-identity-line" style={styles.identityLine}>
            {identityItems.map((item, index) => (
              <React.Fragment key={`${item.label}-${index}`}>
                {index > 0 && <Text style={styles.identitySeparator}>{'\u00A0|\u00A0'}</Text>}
                {item.account ? (
                  <Text
                    onPress={() => onOpenProfile(item.account!.id)}
                    accessibilityRole="link"
                    accessibilityLabel={item.label}
                    style={styles.employerLink}>
                    {item.label}
                  </Text>
                ) : (
                  <Text style={styles.identityText}>{item.label}</Text>
                )}
              </React.Fragment>
            ))}
          </Text>
        )}
        {user.bio && (
          <Text style={styles.bio}>{user.bio}</Text>
        )}
      </View>

      <ProfileSection testID="public-profile-actions-section">
        {/* D-305 — engelliyken mesaj/arama/buluşma satırları davet etmez.
            Sunucu bu istekleri zaten 403 ile reddediyor; dokunulabilir
            bırakmak kullanıcıya sebebi söylemeyen bir hata gösterirdi.
            Fiyatlar görünür kalır — bilgi saklanmıyor, yalnız eylem kapalı. */}
        {blocked && (
          <Text style={styles.blockedNotice} testID="blocked-notice">
            {t('userProfile.blockedNotice', { name: user.name })}
          </Text>
        )}
        <Action styles={styles} disabled={blocked} label={t('userProfile.sendMessage')} price={formatMoney(user.price_message, user.currency, lang)} onPress={() => onMessage([user.name, user.surname].filter(Boolean).join(' '), user.photo_url)} />
        <Action
          styles={styles}
          disabled={blocked}
          label={t('call.schedule')}
          price={formatPerMinute(user.price_call_scheduled ?? user.price_call, user.currency, lang, t('calendar.minutesShort'))}
          onPress={() => onPlanCall(
            [user.name, user.surname].filter(Boolean).join(' '),
            user.photo_url
          )}
        />
        <Action
          styles={styles}
          disabled={blocked}
          label={t('userProfile.buyTime')}
          price={formatPerMinute(user.price_time, user.currency, lang, t('calendar.minutesShort'))}
          onPress={() => onProposeMeetup(
            [user.name, user.surname].filter(Boolean).join(' '),
            user.photo_url
          )}
        />

      </ProfileSection>

      <ProfileSection testID="public-profile-preferences-section">
        {/* 7.6 / D-662 — ayarlardaki anahtar satırıyla aynı: etiket siyah, anahtar
            mavi ve 48'lik hedefin ortasında. Önceden anahtar etiketten yukarıda
            duruyordu ve açıkken siyahtı. */}
        <View style={styles.toggleRow}>
          <Text style={[styles.settingLabel, styles.rowLabel]}>{t('message.translate')}</Text>
          <View style={styles.toggleTarget}>
            <Switch
              testID="contact-translation-switch"
              style={styles.toggle}
              hitSlop={12}
              value={translationEnabled}
              accessibilityLabel={t('message.translate')}
              onValueChange={changeTranslation}
              disabled={translationSaving}
              trackColor={{ false: colors.gray200, true: profilePalette(colors).selectedSurface }}
              thumbColor="#FFFFFF"
            />
          </View>
        </View>
        {!contactPreferencesReady ? (
          <View
            testID="contact-preferences-state"
            style={styles.preferenceLoadState}
            accessibilityLiveRegion="polite">
            {contactPreferencesState === 'error' ? (
              <>
                <Text testID="contact-preferences-error" style={styles.preferenceError}>
                  {t('profile.exceptionsLoadFailed')}
                </Text>
                <TouchableOpacity
                  testID="contact-preferences-retry"
                  style={styles.preferenceRetry}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.retry')}
                  onPress={loadContactPreferences}>
                  <Text style={styles.preferenceRetryText}>{t('common.retry')}</Text>
                </TouchableOpacity>
              </>
            ) : (
              <ActivityIndicator color={colors.black} />
            )}
          </View>
        ) : (
          <>
            {/* Kişi bazlı mesaj istisnası yalnız yeni konuşmanın kurulma kapısını
                ezer. Varsayılan = genel "kimler bana yazabilir" tercihi. */}
            <Text style={styles.settingSubLabel}>{t('profile.messagesFromContact')}</Text>
            <View style={styles.optionRow}>
              {([
                { key: 'default', label: t('profile.callsFromContactDefault'), value: null },
                { key: 'allow', label: t('profile.callsFromContactAllow'), value: true },
                { key: 'block', label: t('profile.callsFromContactBlock'), value: false },
              ] as { key: string; label: string; value: boolean | null }[]).map(({ key, label, value }) => (
                <TouchableOpacity
                  key={key}
                  testID={`message-exception-${key}`}
                  style={[styles.option, messageAllowed === value && styles.optionActive]}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: messageAllowed === value, disabled: messageAllowedSaving }}
                  disabled={messageAllowedSaving}
                  onPress={() => changeMessageAllowed(value)}>
                  <Text
                    style={[styles.optionText, messageAllowed === value && styles.optionTextActive]}
                    numberOfLines={2}
                    adjustsFontSizeToFit
                    minimumFontScale={0.85}>
                    {label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {messageAllowedFeedback && (
              <View
                testID="message-exception-feedback"
                style={styles.preferenceFeedback}
                accessibilityLiveRegion="polite">
                <Text style={messageAllowedFeedback.state === 'error'
                  ? styles.preferenceError : styles.preferenceStatus}>
                  {messageAllowedFeedback.state === 'saving'
                    ? t('profile.saving')
                    : messageAllowedFeedback.state === 'saved'
                    ? t('message.saved')
                    : t('message.saveError')}
                </Text>
                {messageAllowedFeedback.state === 'error' && (
                  <TouchableOpacity
                    testID="message-exception-save-retry"
                    style={styles.inlineRetry}
                    accessibilityRole="button"
                    accessibilityLabel={t('common.retry')}
                    onPress={() => changeMessageAllowed(messageAllowedFeedback.retryValue)}>
                    <Text style={styles.preferenceRetryText}>{t('common.retry')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
            {/* 9.4.7.5 kişi istisnası (D-227): bu kişi için genel "kimler beni
                arayabilir" tercihini ez. Varsayılan = genel ayar. */}
            <Text style={styles.settingSubLabel}>{t('profile.callsFromContact')}</Text>
            <View style={styles.optionRow}>
              {([
                { key: 'default', label: t('profile.callsFromContactDefault'), value: null },
                { key: 'allow', label: t('profile.callsFromContactAllow'), value: true },
                { key: 'block', label: t('profile.callsFromContactBlock'), value: false },
              ] as { key: string; label: string; value: boolean | null }[]).map(({ key, label, value }) => (
                <TouchableOpacity
                  key={key}
                  testID={`call-exception-${key}`}
                  style={[styles.option, callAllowed === value && styles.optionActive]}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: callAllowed === value, disabled: callAllowedSaving }}
                  disabled={callAllowedSaving}
                  onPress={() => changeCallAllowed(value)}>
                  <Text
                    style={[styles.optionText, callAllowed === value && styles.optionTextActive]}
                    numberOfLines={2}
                    adjustsFontSizeToFit
                    minimumFontScale={0.85}>
                    {label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {callAllowedFeedback && (
              <View
                testID="call-exception-feedback"
                style={styles.preferenceFeedback}
                accessibilityLiveRegion="polite">
                <Text style={callAllowedFeedback.state === 'error'
                  ? styles.preferenceError : styles.preferenceStatus}>
                  {callAllowedFeedback.state === 'saving'
                    ? t('profile.saving')
                    : callAllowedFeedback.state === 'saved'
                    ? t('message.saved')
                    : t('message.saveError')}
                </Text>
                {callAllowedFeedback.state === 'error' && (
                  <TouchableOpacity
                    testID="call-exception-save-retry"
                    style={styles.inlineRetry}
                    accessibilityRole="button"
                    accessibilityLabel={t('common.retry')}
                    onPress={() => changeCallAllowed(callAllowedFeedback.retryValue)}>
                    <Text style={styles.preferenceRetryText}>{t('common.retry')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </>
        )}
        <TouchableOpacity
          style={styles.settingRow}
          onPress={contactPriceFailed ? loadContactPrice : openPriceModal}
          disabled={contactPriceLoading}>
          <Text style={[styles.settingLabel, styles.rowLabel]}>{t('message.customPrice')}</Text>
          <Text style={styles.actionPrice} testID="contact-price-value">
            {contactPriceLoading
              ? '…'
              : contactPriceFailed
              ? t('common.retry')
              : customPrice === null
              ? '—'
              : formatMoney(customPrice, user.currency)}
          </Text>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
      </ProfileSection>

      {/* D-305 — tek satır iki durumu taşır. Engelleme yıkıcıdır: kırmızı.
          Kaldırma onarıcıdır: aynı satır, nötr renk. Kaldırmayı da kırmızı
          bırakmak, geri alma eylemini yıkıcı gibi gösterirdi. D-668 — çerçeveli
          düğme değil, Çıkış gibi kendi bölümünde bir satır. */}
      <SectionActionRow
        testID="block-user"
        label={blocked ? t('profile.unblock') : t('userProfile.block')}
        tone={blocked ? 'neutral' : 'danger'}
        onPress={blocked ? doUnblock : doBlock}
        disabled={blockSaving}
      />
      </ScrollView>

      <Modal
        visible={priceModal}
        transparent
        animationType="fade"
        onRequestClose={() => setPriceModal(false)}>
        <KeyboardAvoidingView
          style={styles.keyboardFill}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <TouchableOpacity
            style={styles.sheetOverlay}
            activeOpacity={1}
            onPress={() => setPriceModal(false)}>
            <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
              <Text style={styles.priceModalTitle}>
                {t('message.pricePrompt', { name: user.name })}
              </Text>
              {/* D-302 — beş tür tek ekranda. Küçük ekranda kaydırma olmadan
                  Kaydet düğmesine ulaşılamazdı. */}
              <ScrollView
                style={styles.priceModalScroll}
                keyboardShouldPersistTaps="handled">
                <Text style={styles.priceFieldLabel}>{t('profile.priceMessage')}</Text>
                <TextInput
                  style={styles.priceModalInput}
                  value={priceInput}
                  onChangeText={setPriceInput}
                  keyboardType="decimal-pad"
                  placeholder="0"
                  placeholderTextColor={colors.gray400}
                  autoFocus
                />
                {/* Boş bırakılan tür = özel fiyat yok; yer tutucu bunu söyler,
                    böylece "0" (ücretsiz) ile "boş" (genel fiyat) karışmaz. */}
                {CONTACT_PRICE_KINDS.map(({ key, labelKey }) => (
                  <View key={key}>
                    <Text style={styles.priceFieldLabel}>{t(labelKey)}</Text>
                    <TextInput
                      style={styles.priceModalInput}
                      value={extraInputs[key] ?? ''}
                      onChangeText={(text) =>
                        setExtraInputs((prev) => ({ ...prev, [key]: text }))}
                      keyboardType="decimal-pad"
                      placeholder={t('message.priceUsesGeneral')}
                      placeholderTextColor={colors.gray400}
                      testID={`contact-${key}`}
                    />
                  </View>
                ))}
              </ScrollView>
              <TouchableOpacity
                style={[styles.sheetAction, priceSaving && styles.disabledAction]}
                onPress={saveContactPrice}
                disabled={priceSaving}>
                <Text style={styles.sheetActionText}>{t('common.save')}</Text>
              </TouchableOpacity>
              {/* Yalnız gerçekten özel fiyatı olan kişide görünür: olmayan bir
                  kaydı "çıkar" demek kullanıcıya var olmayan bir bağ gösterirdi. */}
              {customPrice !== null && (
                <TouchableOpacity
                  style={[styles.sheetAction, priceSaving && styles.disabledAction]}
                  onPress={removeContactPrice}
                  disabled={priceSaving}
                  testID="remove-contact-price">
                  <Text style={styles.sheetActionDanger}>{t('message.priceRemove')}</Text>
                </TouchableOpacity>
              )}
            </View>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

function Action({
  label,
  price,
  onPress,
  styles,
  disabled = false,
}: {
  label: string;
  price: string;
  onPress: () => void;
  styles: ReturnType<typeof makeStyles>;
  disabled?: boolean;
}) {
  return (
    <TouchableOpacity
      style={[styles.actionRow, disabled && styles.disabledAction]}
      onPress={onPress}
      disabled={disabled}
      accessibilityState={{ disabled }}>
      <Text testID="profile-action-label" style={styles.actionLabel} numberOfLines={2}>{label}</Text>
      <Text testID="profile-action-price" style={styles.actionPrice} numberOfLines={2}>{price}</Text>
    </TouchableOpacity>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.white,
  },
  errorText: { fontSize: fontSize.md, color: colors.gray400, marginBottom: spacing.md },
  retryBtn: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    backgroundColor: colors.black,
  },
  retryText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
  profileSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
  },
  summaryText: {
    flex: 1,
    marginLeft: spacing.xl,
  },
  photo: {
    width: 104,
    height: 104,
    borderRadius: 52,
  },
  info: {
    alignItems: 'flex-start',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  // 9.26 / D-641 — rozet adın hemen yanında, satır sonunda değil.
  nameWithBadge: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' },
  name: {
    fontSize: fontSize.md,
    fontWeight: '700',
    color: colors.black,
    marginBottom: spacing.xs,
  },
  identityLine: {
    fontSize: fontSize.md,
    color: colors.gray600,
    lineHeight: 22,
  },
  identityText: {
    fontSize: fontSize.md,
    color: colors.gray600,
  },
  identitySeparator: {
    fontSize: fontSize.md,
    color: colors.gray400,
  },
  employerLink: {
    fontSize: fontSize.md,
    color: '#2878d4',
  },
  bio: {
    fontSize: fontSize.md,
    color: colors.gray600,
    textAlign: 'left',
    marginTop: spacing.md,
    lineHeight: 22,
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.md,
    minHeight: 48,
  },
  actionLabel: {
    flex: 1,
    minWidth: 0,
    marginRight: spacing.md,
    fontSize: fontSize.md,
    color: profilePalette(colors).heading,
    fontWeight: '500',
  },
  actionPrice: {
    maxWidth: '48%',
    flexShrink: 1,
    fontSize: fontSize.md,
    color: colors.gray400,
    textAlign: 'right',
  },
  // Kişi istisnası radyoları — Profil'deki "kimler beni arayabilir" ile aynı
  // geometri (D-198 yeknesaklık kuralı).
  settingSubLabel: { fontSize: fontSize.md, color: colors.black, marginTop: spacing.md },
  optionRow: { flexDirection: 'row', gap: spacing.sm, paddingVertical: spacing.sm },
  option: {
    flex: 1,
    minWidth: 0,
    minHeight: 44,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: profilePalette(colors).optionBorder,
    backgroundColor: profilePalette(colors).optionSurface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // 7.6 / D-662 — ayarlardaki seçenek çipleriyle aynı: hap biçimi, seçili mavi
  // (profilePalette). Önceden seçili siyahtı; aynı türden seçim iki renkteydi.
  optionActive: {
    backgroundColor: profilePalette(colors).selectedSurface,
    borderColor: profilePalette(colors).selectedSurface,
  },
  optionText: { fontSize: 14, color: colors.gray600, textAlign: 'center' },
  optionTextActive: { color: profilePalette(colors).selectedText, fontWeight: '600' },
  toggleRow: { flexDirection: 'row', alignItems: 'center', minHeight: 48 },
  toggleTarget: { width: 56, height: 48, flexShrink: 0, alignItems: 'center', justifyContent: 'center' },
  toggle: { transform: [{ scale: 0.85 }] },
  settingLabel: { fontSize: fontSize.md, color: colors.black },
  rowLabel: { flex: 1, minWidth: 0, marginRight: spacing.md },
  rowChevron: { flexShrink: 0, width: 12, textAlign: 'center', fontSize: fontSize.md, color: colors.gray600, marginLeft: spacing.sm },
  preferenceLoadState: {
    minHeight: 92,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
  },
  preferenceFeedback: {
    minHeight: 28,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  preferenceStatus: { fontSize: fontSize.sm, color: colors.gray600 },
  preferenceError: { fontSize: fontSize.sm, color: '#c62828', textAlign: 'center' },
  preferenceRetry: { minHeight: 44, justifyContent: 'center', paddingHorizontal: spacing.md },
  inlineRetry: { minHeight: 44, justifyContent: 'center', marginLeft: spacing.sm },
  preferenceRetryText: { fontSize: fontSize.sm, color: colors.black, fontWeight: '600' },
  settingRow: {
    minHeight: 58,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  keyboardFill: { flex: 1 },
  sheetOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  sheet: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: spacing.lg,
  },
  priceModalTitle: {
    fontSize: fontSize.md,
    color: colors.black,
    textAlign: 'center',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.md,
  },
  priceModalScroll: {
    maxHeight: 320,
  },
  priceFieldLabel: {
    fontSize: fontSize.sm,
    color: colors.gray600,
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  priceModalInput: {
    height: 48,
    borderWidth: 1,
    borderColor: colors.gray200,
    borderRadius: radius.control,
    paddingHorizontal: spacing.md,
    fontSize: fontSize.md,
    color: colors.black,
    textAlign: 'center',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
  },
  sheetAction: {
    minHeight: 52,
    alignItems: 'center',
    justifyContent: 'center',
    borderTopWidth: 1,
    borderTopColor: colors.gray100,
  },
  sheetActionText: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
  sheetActionDanger: { fontSize: fontSize.md, fontWeight: '600', color: '#D94A45' },
  disabledAction: { opacity: 0.5 },
  blockedNotice: {
    fontSize: fontSize.sm,
    color: colors.gray600,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
  },
});
